import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Trophy, Clock, Users, DollarSign, Zap, Crown, ShieldCheck, Lock, Coins, Gem } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { SolTutorial } from "@/components/DailyWordVolt";
import { useAudio, useTrack } from "@/context/GameFeel";
import { VoltSay } from "@/components/Volt";
import { intro, quip, personaFor } from "@/lib/volt";

const fmtLeft = (ends) => {
  const diff = new Date(ends) - new Date();
  if (Number.isNaN(diff)) return "—";
  if (diff <= 0) return "Ended";
  const d = Math.floor(diff / 8.64e7), h = Math.floor((diff % 8.64e7) / 3.6e6), m = Math.floor((diff % 3.6e6) / 6e4), s = Math.floor((diff % 6e4) / 1000);
  return d > 0 ? `${d}d ${h}h ${m}m` : `${h}h ${m}m ${s}s`;
};

function Countdown({ ends }) {
  const [left, setLeft] = useState(() => fmtLeft(ends));
  useEffect(() => {
    setLeft(fmtLeft(ends));
    const iv = setInterval(() => setLeft(fmtLeft(ends)), 1000);
    return () => clearInterval(iv);
  }, [ends]);
  return <span className="font-mono" data-testid="tournament-countdown">{left}</span>;
}

function SkillGame({ tournament, onClose, onSubmitted }) {
  const { sfx } = useAudio();
  useTrack("play");
  const [voltLine, setVoltLine] = useState(null);
  const onScored = (data) => { sfx("fanfare"); setTimeout(() => sfx("coin"), 350); setVoltLine(quip({ outcome: "win", persona: personaFor(tournament.game) })); };
  const [session, setSession] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [input, setInput] = useState("");
  const [time, setTime] = useState(60);
  const [phase, setPhase] = useState("loading"); // loading, play, done
  const [result, setResult] = useState(null);
  const timerRef = useRef(null);
  const [rIdx, setRIdx] = useState(0);
  const [solved, setSolved] = useState({});
  const [pick21, setPick21] = useState([]);
  const [wheelSteps, setWheelSteps] = useState(0);
  const [wvWords, setWvWords] = useState([]);
  const [wvInput, setWvInput] = useState("");
  const [wvMsg, setWvMsg] = useState(null);
  const wvTotal = wvWords.reduce((s, w) => s + w.points, 0);
  const isCard21 = session?.game === "card_21";
  const isWheel = session?.game === "precision_wheel";
  const isWord = session?.game === "word_volt";
  const isSol = session?.game === "solitaire_sprint";
  const [solChain, setSolChain] = useState([]);
  const [showSolTut, setShowSolTut] = useState(() => { try { return !localStorage.getItem("sol_tut_seen"); } catch { return false; } });
  const dismissSolTut = () => { try { localStorage.setItem("sol_tut_seen", "1"); } catch { /* noop */ } setShowSolTut(false); };
  const solCur = session?.board ? (solChain.length ? session.board.cards[solChain[solChain.length - 1]].r : session.board.start.r) : null;
  const solOk = (a, b) => { const d = Math.abs(a - b); return d === 1 || d === 12; };
  const isBoard = isCard21 || isWheel;
  const gameLabel = isCard21 ? "Card 21" : isWheel ? "Precision Wheel" : isWord ? "WordVolt" : isSol ? "Solitaire Sprint" : "Speed Math";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post(`/tournaments/${tournament.tournament_id}/play`);
        setSession(data);
        setTime(data.duration);
        setPhase("play");
      } catch (e) {
        toast.error(apiErr(e));
        onClose();
      }
    })();
  }, []);

  useEffect(() => {
    if (phase !== "play") return;
    if (isSol && showSolTut) return; // paused until solitaire tutorial dismissed
    timerRef.current = setInterval(() => setTime((t) => t - 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [phase, isSol, showSolTut]);

  useEffect(() => {
    if (time <= 0 && phase === "play") isWord ? submitWords() : isSol ? submitSol() : submit();
  }, [time]);

  const opSym = { "+": "+", "-": "−", x: "×" };
  const cur = session?.problems?.[idx];

  const answer = (val) => {
    const na = { ...answers, [idx]: parseInt(val) };
    setAnswers(na);
    setInput("");
    if (idx < session.problems.length - 1) setIdx(idx + 1);
  };

  const submit = async (boardAns) => {
    clearInterval(timerRef.current);
    setPhase("done");
    try {
      const payload = isBoard ? (boardAns || solved) : Object.fromEntries(Object.entries(answers).map(([k, v]) => [k, v]));
      const { data } = await api.post(`/tournaments/${tournament.tournament_id}/submit`, {
        session_id: session.session_id, answers: payload,
      });
      setResult(data); onScored(data);
      onSubmitted();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const toggleCard = (i) => setPick21((p) => p.includes(i) ? p.filter((x) => x !== i) : [...p, i]);
  const lockCard21 = () => {
    const na = { ...solved, [rIdx]: pick21 }; setSolved(na); setPick21([]);
    if (rIdx < session.rounds.length - 1) setRIdx(rIdx + 1); else submit(na);
  };
  const stepWheel = () => {
    const rd = session.rounds[rIdx]; const ns = wheelSteps + 1; setWheelSteps(ns);
    if ((rd.start + ns) % rd.segments === rd.target) {
      const na = { ...solved, [rIdx]: ns }; setSolved(na); setWheelSteps(0);
      if (rIdx < session.rounds.length - 1) setRIdx(rIdx + 1); else submit(na);
    }
  };
  const lockWheel = () => {
    const na = { ...solved, [rIdx]: wheelSteps }; setSolved(na); setWheelSteps(0);
    if (rIdx < session.rounds.length - 1) setRIdx(rIdx + 1); else submit(na);
  };

  const addWord = async () => {
    const w = wvInput.trim().toUpperCase();
    setWvInput("");
    if (w.length < 3) { setWvMsg({ ok: false, text: "Min 3 letters" }); return; }
    if (wvWords.some((x) => x.word === w)) { setWvMsg({ ok: false, text: `${w} already used` }); return; }
    try {
      const { data } = await api.post("/duels/wordvolt/check", { session_id: session.session_id, word: w });
      if (data.valid) { setWvWords((p) => [{ word: data.word, points: data.points }, ...p]); setWvMsg({ ok: true, text: `${data.word} +${data.points}` }); }
      else setWvMsg({ ok: false, text: `${w}: ${data.reason}` });
    } catch { setWvMsg({ ok: false, text: "Could not check word" }); }
  };
  const submitWords = async () => {
    clearInterval(timerRef.current); setPhase("done");
    try {
      const { data } = await api.post(`/tournaments/${tournament.tournament_id}/submit`, {
        session_id: session.session_id, answers: { words: wvWords.map((w) => w.word) },
      });
      setResult(data); onScored(data); onSubmitted();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const tapSolCard = (i) => {
    if (solChain.includes(i) || !solOk(session.board.cards[i].r, solCur)) return;
    setSolChain((p) => [...p, i]);
  };
  const submitSol = async () => {
    clearInterval(timerRef.current); setPhase("done");
    try {
      const { data } = await api.post(`/tournaments/${tournament.tournament_id}/submit`, {
        session_id: session.session_id, answers: { moves: solChain },
      });
      setResult(data); onScored(data); onSubmitted();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-lg glass rounded-3xl border border-emerald-500/30 glow-cash p-8 relative">
        {isSol && phase === "play" && showSolTut && <SolTutorial onClose={dismissSolTut} />}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2 text-emerald-400"><Zap className="w-5 h-5" /><span className="font-display font-bold">{gameLabel}</span></div>
          {phase === "play" && <div className={`font-mono text-2xl font-black ${time <= 10 ? "text-red-400" : "text-emerald-300"}`}>{time}s</div>}
        </div>

        {phase === "loading" && <p className="text-center py-12 text-gray-400">Loading…</p>}

        {phase === "play" && isCard21 && session?.rounds?.[rIdx] && (() => {
          const rd = session.rounds[rIdx];
          const sum = pick21.reduce((s, i) => s + rd.cards[i], 0);
          const face = (v) => v === 11 ? "A" : String(v);
          return (
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-1">{Object.keys(solved).length}/{session.rounds.length} · total {sum}</p>
              <p className="text-sm text-gray-400 mb-3">Pick cards that total exactly <span className="text-emerald-300 font-bold">21</span></p>
              <div className="flex flex-wrap justify-center gap-2 mb-4">
                {rd.cards.map((v, i) => (
                  <button key={i} data-testid={`tcard21-card-${i}`} onClick={() => toggleCard(i)}
                    className={`w-14 h-20 rounded-lg border-2 flex items-center justify-center text-2xl font-black transition-colors duration-150 ${pick21.includes(i) ? "bg-emerald-500/25 border-emerald-400 text-emerald-200" : "bg-white/90 border-white/30 text-gray-900"}`}>{face(v)}</button>
                ))}
              </div>
              <button data-testid="tcard21-lock" onClick={lockCard21}
                className={`px-6 py-2.5 rounded-xl font-semibold ${sum === 21 ? "bg-gradient-to-r from-emerald-500 to-cyan-600" : "bg-white/10 text-gray-300"}`}>{sum === 21 ? "Score 21!" : "Lock In / Skip"}</button>
              <p className="text-xs text-gray-600 mt-3">Pure skill — no random payouts.</p>
            </div>
          );
        })()}

        {phase === "play" && isWheel && session?.rounds?.[rIdx] && (() => {
          const rd = session.rounds[rIdx];
          const pos = (rd.start + wheelSteps) % rd.segments;
          return (
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-1">{Object.keys(solved).length}/{session.rounds.length}</p>
              <p className="text-sm text-gray-400 mb-3">Advance the pointer onto segment <span className="text-emerald-300 font-bold">{rd.target}</span></p>
              <div className="relative w-52 h-52 mx-auto mb-4">
                {Array.from({ length: rd.segments }).map((_, s) => {
                  const angle = (s / rd.segments) * 360;
                  return (
                    <div key={s} style={{ transform: `rotate(${angle}deg) translateY(-92px)` }} className="absolute left-1/2 top-1/2 -ml-4 -mt-4">
                      <div style={{ transform: `rotate(${-angle}deg)` }} className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${s === pos ? "bg-amber-400 text-black" : s === rd.target ? "bg-emerald-500/40 text-emerald-100 border border-emerald-400" : "bg-black/50 text-gray-400 border border-white/10"}`}>{s}</div>
                    </div>
                  );
                })}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-24 h-24 rounded-full border-2 border-emerald-500/30 flex items-center justify-center font-mono text-3xl font-black text-amber-300">{pos}</div>
                </div>
              </div>
              <div className="flex gap-2 justify-center">
                <button data-testid="twheel-step" onClick={stepWheel} className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-600 font-semibold">Spin +1</button>
                <button data-testid="twheel-lock" onClick={lockWheel} className="px-5 py-2.5 rounded-xl bg-white/10 border border-white/10 font-semibold">Skip</button>
              </div>
            </div>
          );
        })()}

        {phase === "play" && isWord && session?.rack && (
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-3">{wvWords.length} words · <span className="text-emerald-300 font-mono">{wvTotal} pts</span></p>
            <div className="flex justify-center gap-2 mb-4 flex-wrap">
              {session.rack.map((ch, i) => (
                <div key={i} data-testid={`twv-tile-${i}`}
                  className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-cyan-600/20 border-2 border-emerald-500/40 flex items-center justify-center text-2xl font-black text-emerald-100">{ch}</div>
              ))}
            </div>
            <input data-testid="twv-input" autoFocus value={wvInput}
              onChange={(e) => setWvInput(e.target.value.replace(/[^a-zA-Z]/g, ""))}
              onKeyDown={(e) => { if (e.key === "Enter") addWord(); }}
              className="w-56 text-center uppercase tracking-widest bg-black/50 border-2 border-emerald-500/40 rounded-xl px-4 py-3 text-2xl font-mono focus:border-emerald-500 outline-none mb-2" placeholder="TYPE" />
            <div className="h-5 mb-2">{wvMsg && <span className={`text-sm font-mono ${wvMsg.ok ? "text-emerald-400" : "text-red-400"}`}>{wvMsg.text}</span>}</div>
            <div className="flex gap-2 justify-center mb-3">
              <button data-testid="twv-add" onClick={addWord} className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-600 font-semibold">Submit word</button>
              <button data-testid="twv-done" onClick={submitWords} className="px-5 py-2.5 rounded-xl bg-white/10 border border-white/10 font-semibold">Finish</button>
            </div>
            <div className="flex flex-wrap justify-center gap-1.5 max-h-24 overflow-y-auto hide-scroll">
              {wvWords.map((w) => (
                <span key={w.word} className="px-2 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-xs font-mono text-emerald-200">{w.word} <span className="text-emerald-400">+{w.points}</span></span>
              ))}
            </div>
            <p className="text-xs text-gray-600 mt-3">Same letters for everyone — build the highest-scoring words. Pure vocabulary skill.</p>
          </div>
        )}

        {phase === "play" && isSol && session?.board && (
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-2">Chain: <span className="text-emerald-300 font-mono">{solChain.length}</span> · tap a card ±1 rank from the active card (A wraps K)</p>
            <div className="mb-3 flex justify-center">
              <div data-testid="tsol-active" className="w-16 h-20 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center text-3xl font-black text-white">{session.board.cards[solChain[solChain.length - 1]]?.label ?? session.board.start.label}</div>
            </div>
            <div className="grid grid-cols-6 gap-1.5 mb-4">
              {session.board.cards.map((c, i) => {
                const used = solChain.includes(i);
                const playable = !used && solOk(c.r, solCur);
                return (
                  <button key={i} data-testid={`tsol-card-${i}`} onClick={() => tapSolCard(i)} disabled={used || !playable}
                    className={`h-12 rounded-lg text-lg font-black transition-all duration-150 ${used ? "opacity-15 bg-white/5" : playable ? "bg-white/10 border-2 border-emerald-500/60 text-emerald-100" : "bg-white/5 border border-white/10 text-gray-500"}`}>
                    {c.label}
                  </button>
                );
              })}
            </div>
            <button data-testid="tsol-done" onClick={submitSol} className="px-6 py-2.5 rounded-xl bg-white/10 border border-white/10 font-semibold">Finish</button>
          </div>
        )}

        {phase === "play" && !isBoard && !isWord && !isSol && cur && (
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-2">Problem {idx + 1} / {session.problems.length} · {Object.keys(answers).length} answered</p>
            <div className="font-mono font-black text-5xl sm:text-6xl mb-6">{cur.a} {opSym[cur.op]} {cur.b}</div>
            <input data-testid="skill-answer-input" autoFocus type="number" value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && input !== "") answer(input); }}
              className="w-40 text-center bg-black/50 border-2 border-emerald-500/40 rounded-xl px-4 py-4 text-3xl font-mono focus:border-emerald-500 outline-none mb-6" placeholder="?" />
            <div className="flex gap-3">
              <button onClick={() => answer(input || 0)} data-testid="skill-answer-submit" disabled={input === ""}
                className="flex-1 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-600 font-bold disabled:opacity-40">Submit</button>
              <button onClick={submit} className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm">Finish</button>
            </div>
          </div>
        )}

        {phase === "done" && (
          <div className="text-center py-4">
            <Trophy className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
            <h3 className="font-display text-3xl font-black mb-2">Score: {result?.score ?? "…"}</h3>
            <p className="text-gray-400 mb-1">Best score: {result?.best_score}</p>
            {result?.rank && <p className="text-emerald-300 mb-6">Currently ranked #{result.rank} of {result.total_players}</p>}
            <p className="text-xs text-gray-500 mb-4">Prizes are distributed when the tournament ends.</p>
            {voltLine && <div className="mb-6"><VoltSay testid="volt-tournament-result" text={voltLine} mood="cheer" label="Volt · hype" accent="emerald" size={52} /></div>}
            <button data-testid="skill-close-button" onClick={onClose} className="px-6 py-3 rounded-xl bg-white/5 border border-white/10">Back to Tournaments</button>
          </div>
        )}
      </motion.div>
    </div>
  );
}

export default function Tournaments() {
  const { user, refresh } = useAuth();
  const { t } = useT();
  const [shout] = useState(() => intro("tournament"));
  const [list, setList] = useState([]);
  const [playing, setPlaying] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDiamond, setIsDiamond] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/tournaments"); setList(data); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  const loadVip = async () => {
    try { const { data } = await api.get("/loyalty"); setIsDiamond((data.tier_index ?? 0) >= 4); }
    catch { /* ignore */ }
  };
  useEffect(() => { load(); loadVip(); }, []);

  const join = async (t) => {
    try {
      await api.post(`/tournaments/${t.tournament_id}/join`);
      await refresh();
      const fee = t.currency === "coins" ? `${Number(t.entry_fee).toLocaleString()} coins` : `$${t.entry_fee}`;
      toast.success(`Joined ${t.name}! Entry fee ${fee} deducted.`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const cashOn = !!user?.cash_enabled;
  const eligible = cashOn ? (user?.geo_allowed_cash && user?.age_verified) : true;

  return (
    <div className="p-4 lg:p-8">
      <div className={`relative rounded-3xl overflow-hidden mb-8 border ${cashOn ? "border-emerald-500/20 glow-cash" : "border-amber-500/20 glow-coins"}`}>
        <img decoding="async" fetchPriority="high" src="https://images.unsplash.com/photo-1633545495735-25df17fb9f31?crop=entropy&cs=srgb&fm=jpg&q=70&w=1200" alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
        <div className="relative z-10 p-6 lg:p-12">
          <span data-testid="tournaments-mode-badge" className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4 ${cashOn ? "bg-emerald-500/15 border border-emerald-500/30 text-emerald-300" : "bg-amber-500/15 border border-amber-500/30 text-amber-300"}`}>
            {cashOn ? <><DollarSign className="w-3.5 h-3.5" /> Real Cash Prizes · Skill Only</> : <><Coins className="w-3.5 h-3.5" /> Fun Coin Prizes · Skill Only · No monetary value</>}
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-3">
            <span className={cashOn ? "text-gradient-cash" : "text-gradient-coins"}>{cashOn ? t("tourneys_title") : t("tourneys_title_coins")}</span>
          </h1>
          <p className="text-gray-400 max-w-xl">{cashOn ? t("tourneys_sub") : t("tourneys_sub_coins")}</p>
        </div>
      </div>

      <div className="mb-8"><VoltSay testid="volt-tournament-shout" text={shout} mood="cheer" label="Volt · leaderboard shout-out" size={64} /></div>

      {cashOn && !eligible && (
        <div className="glass rounded-2xl p-5 border border-red-500/30 mb-6 flex items-start gap-3">
          <Lock className="w-5 h-5 text-red-400 mt-0.5" />
          <div>
            <p className="font-semibold text-red-300">Cash tournaments unavailable</p>
            <p className="text-sm text-gray-400">
              {!user?.geo_allowed_cash ? "Paid skill games are restricted in your region. " : ""}
              {!user?.age_verified ? "Complete age verification to participate." : ""}
              You can still enjoy the full Fun Coins casino.
            </p>
          </div>
        </div>
      )}

      {loading ? <p className="text-gray-500">Loading tournaments…</p> : (
        <div data-testid="tournament-lobby-section" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {list.map((t) => {
            const coins = t.currency === "coins";
            const vipLocked = t.vip && !isDiamond;
            const joinBlocked = t.status !== "open" || vipLocked || (!coins && !eligible);
            const feeLabel = coins ? `${Number(t.entry_fee).toLocaleString()} coins` : `$${t.entry_fee}`;
            return (
            <motion.div key={t.tournament_id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
              data-testid="tournament-card"
              className={`glass rounded-2xl p-6 relative overflow-hidden ${t.vip ? "border border-amber-400/40 shadow-[0_0_24px_rgba(251,191,36,0.15)]" : "border border-emerald-500/20"}`}>
              {t.vip && (
                <div data-testid="vip-tournament-badge" className="absolute top-0 right-0 flex items-center gap-1.5 px-3 py-1.5 rounded-bl-xl bg-gradient-to-r from-amber-400 to-fuchsia-500 text-black text-xs font-black tracking-wide">
                  <Gem className="w-3.5 h-3.5" /> DIAMOND VIP
                </div>
              )}
              <div className="flex items-start justify-between mb-4">
                <div className={t.vip ? "pr-24" : ""}>
                  <h3 className="font-display text-2xl font-bold">{t.name}</h3>
                  <p className="text-sm text-gray-400 mt-1">{t.description}</p>
                </div>
                {!t.vip && (
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${t.status === "open" ? "bg-emerald-500/15 text-emerald-300" : "bg-white/10 text-gray-400"}`}>
                    {t.status.toUpperCase()}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-black/30 rounded-xl p-3 text-center">
                  <p className="text-xs text-gray-500 mb-1">Prize Pool</p>
                  <p className={`font-mono font-black text-lg flex items-center justify-center gap-1 ${coins ? "text-amber-400" : "text-emerald-400"}`}>
                    {coins ? <Coins className="w-4 h-4" /> : "$"}{Number(t.prize_pool).toLocaleString()}
                  </p>
                </div>
                <div className="bg-black/30 rounded-xl p-3 text-center">
                  <p className="text-xs text-gray-500 mb-1">Entry</p>
                  <p className={`font-mono font-black text-lg flex items-center justify-center gap-1 ${coins ? "text-amber-300" : ""}`}>
                    {coins ? <Coins className="w-4 h-4" /> : "$"}{Number(t.entry_fee).toLocaleString()}
                  </p>
                </div>
                <div className="bg-black/30 rounded-xl p-3 text-center">
                  <p className="text-xs text-gray-500 mb-1 flex items-center justify-center gap-1"><Users className="w-3 h-3" />Players</p>
                  <p className="font-mono font-black text-lg">{t.player_count}/{t.max_players}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-400 mb-4" data-testid="tournament-timing">
                <Clock className="w-4 h-4 text-emerald-400" />
                {t.status === "completed" || new Date(t.ends_at) <= new Date()
                  ? <>Ended {new Date(t.ends_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</>
                  : <>Ends in <Countdown ends={t.ends_at} /></>}
              </div>

              {/* mini leaderboard */}
              <div className="bg-black/20 rounded-xl p-3 mb-4">
                <p className="text-xs text-gray-500 mb-2 flex items-center gap-1" data-testid="standings-label"><Crown className="w-3 h-3 text-amber-400" /> {t.status === "completed" ? "Final Standings" : "Live Standings"}</p>
                {t.leaderboard.slice(0, 3).map((p, i) => (
                  <div key={i} className="flex justify-between text-sm py-0.5">
                    <span className={p.user_id === user?.user_id ? "text-emerald-300 font-semibold" : "text-gray-300"}>
                      #{i + 1} {p.name}{p.is_bot ? " 🤖" : ""}{p.user_id === user?.user_id ? " (you)" : ""}
                    </span>
                    <span className="font-mono text-gray-400">{p.score}</span>
                  </div>
                ))}
                {t.leaderboard.length === 0 && <p className="text-xs text-gray-600">{t.status === "completed" ? "No entries." : "No entries yet — be first."}</p>}
              </div>

              {t.status === "completed" ? (
                <div className="bg-emerald-500/10 rounded-xl p-3 text-sm">
                  <p className="font-semibold text-emerald-300 mb-1">🏆 Final Results</p>
                  {(!t.prize_distribution || t.prize_distribution.length === 0) && <p className="text-xs text-gray-500">No prizes awarded — no eligible entrants.</p>}
                  {t.prize_distribution?.map((d) => (
                    <div key={d.rank} className="flex justify-between text-gray-300"><span>#{d.rank} {d.name}</span><span className={`font-mono ${coins ? "text-amber-400" : "text-emerald-400"}`}>{coins ? `${Number(d.prize).toLocaleString()} coins` : `$${d.prize}`}</span></div>
                  ))}
                </div>
              ) : t.joined ? (
                <button data-testid="play-skill-button" onClick={() => setPlaying(t)}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-600 font-bold hover:opacity-90 transition-opacity duration-200">
                  Play Skill Game
                </button>
              ) : vipLocked ? (
                <div>
                  <button data-testid="vip-locked-button" disabled
                    className="w-full py-3 rounded-xl bg-white/5 border border-amber-400/30 text-amber-300 font-bold flex items-center justify-center gap-2 opacity-80 cursor-not-allowed">
                    <Lock className="w-4 h-4" /> Diamond VIP only
                  </button>
                  <p className="text-xs text-gray-500 mt-2 text-center">Reach <span className="text-amber-300 font-semibold">Diamond tier</span> in loyalty to unlock this exclusive event.</p>
                </div>
              ) : (
                <button data-testid="join-tournament-button" onClick={() => join(t)} disabled={joinBlocked}
                  className={`w-full py-3 rounded-xl font-bold hover:opacity-90 transition-opacity duration-200 disabled:opacity-40 flex items-center justify-center gap-2 ${t.vip ? "bg-gradient-to-r from-amber-400 to-fuchsia-500 text-black" : "bg-gradient-to-r from-emerald-500 to-cyan-600"}`}>
                  {t.vip ? <Gem className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />} Join · {feeLabel}
                </button>
              )}
            </motion.div>
            );
          })}
        </div>
      )}

      <AnimatePresence>
        {playing && <SkillGame tournament={playing} onClose={() => { setPlaying(null); load(); }} onSubmitted={load} />}
      </AnimatePresence>
    </div>
  );
}
